import React, { useEffect, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux/hooks';
import SmashSpinner from '../../../components/SmashSpinner/SmashSpinner';
import { useForm } from "react-hook-form";
import { validatePasswordLength } from '../../../shared/validation/length';
import { passwordState, updatePassword, resetUpdatedPassword } from '../../../features/password/passwordSlice';
import { login, selectedUser } from '../../auth/authSlice';

interface FormProps {
    handleClose?: () => void
}


const passwordStrengthRegexps = {
    strong: /^(?=(.*[0-9]))(?=.*[\!@#$%^&*()\\[\]{}\-_+=~`|:;"'<>,./?])(?=.*[a-z])(?=(.*[A-Z]))(?=(.*)).{8,}/,
    medium: /^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[a-z])(?=.*[\!@#$%^&*()\\[\]{}\-_+=~`|:;"'<>,./?]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})/,
};

function passwordStrength(input?: string): string | null {
    if (input) {
        if (input.match(passwordStrengthRegexps.strong)) {
            return 'PASSWORD_STRENGTH_STRONG';
        }
        if (input.match(passwordStrengthRegexps.medium)) {
            return 'PASSWORD_STRENGTH_MEDIUM';
        }
        return 'PASSWORD_STRENGTH_LOW';
    } else {
        return null;
    }
}

const PasswordUpdateForm = (props: FormProps) => {
    const { handleClose } = props;
    const dispatch = useAppDispatch();
    type FormValues = {
        currentPassword: string;
        newPassword: string;
        confirmPassword: string;
    };

    const { handleSubmit, setError, formState: { errors, isValid }, watch, register, getValues, getFieldState, reset } = useForm<FormValues>({        
        mode: "onTouched",
        reValidateMode: "onChange"
    });

    const watchNewPassword = watch('newPassword');

    const submitHandler = (values: FormValues) => {
        const passwordToUpdate = {
            currentPassword: values.currentPassword,
            newPassword: values.confirmPassword,
        };

        dispatch(updatePassword(passwordToUpdate));
    }
    const { identity } = useAppSelector(selectedUser);
    const { updatedPassword } = useAppSelector(passwordState);
    useEffect(() => {
        if (updatedPassword.isError) {
            setError('currentPassword', { type: 'invalid' });
            dispatch(resetUpdatedPassword());
        }

        if (updatedPassword.isSuccess && identity?.username) {
            // need to resign user ...
            dispatch(login({ username: identity.username, password: getValues('newPassword') }));
            reset();
            dispatch(resetUpdatedPassword());
        }

        if (watchNewPassword) {
            passwordStrength(watchNewPassword)
        }
    }, [updatedPassword, identity, watchNewPassword, dispatch]);

    if (updatedPassword.isLoading) {
        return < SmashSpinner />;
    }

    return (
        <>
            <Form noValidate onSubmit={handleSubmit(submitHandler)}>
                <Form.Group className="mb-3" controlId="currentPassword">
                    <Form.Control
                        {...register('currentPassword', { required: true, validate: validatePasswordLength })}
                        type="password" placeholder="Current password"
                        isInvalid={!!errors.currentPassword}
                    />
                    {errors.currentPassword?.type === "validate" && (
                        <Form.Control.Feedback type="invalid">
                            Your password must contain at least 4 char
                        </Form.Control.Feedback>)}
                    {errors.currentPassword?.type === "required" && (
                        <Form.Control.Feedback type="invalid">
                            The password is required
                        </Form.Control.Feedback>)}
                </Form.Group>
                <Form.Group className="mb-3" controlId="newPassword">
                    <Form.Control
                        {...register('newPassword', { required: true, validate: validatePasswordLength })}
                        type="password" placeholder="New password"
                        isInvalid={!!errors.newPassword}
                    />
                    {errors.newPassword?.type === "required" && (
                        <Form.Control.Feedback type="invalid">
                            The password is required
                        </Form.Control.Feedback>)}
                    <Form.Control.Feedback>Ok !</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" controlId="confirmPassword">
                    <Form.Control
                        {...register('confirmPassword', { required: true, validate: (val) => getValues("newPassword") === val })}
                        type="password" placeholder="Re-type new password"
                        isInvalid={!!errors.confirmPassword}
                    />
                    {errors.confirmPassword?.type === "validate" && (
                        <Form.Control.Feedback type="invalid">
                            Password do not match
                        </Form.Control.Feedback>)}
                    {errors.confirmPassword?.type === "required" && (
                        <Form.Control.Feedback type="invalid">
                            The password is required
                        </Form.Control.Feedback>)}
                    {!getFieldState('confirmPassword').error && (<Form.Control.Feedback>Ok !</Form.Control.Feedback>)}
                </Form.Group>

                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                    <Button variant="light" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={!isValid}>
                        Save
                    </Button>
                </div>
            </Form>
        </>
    )
}

export default PasswordUpdateForm
